import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, vModelSelect as _vModelSelect, withDirectives as _withDirectives, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, withCtx as _withCtx, createBlock as _createBlock, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "forecast-accuracy-modal__header" }
const _hoisted_2 = { class: "forecast-accuracy-modal__title" }
const _hoisted_3 = { class: "forecast-accuracy-modal__controls" }
const _hoisted_4 = { value: "1" }
const _hoisted_5 = { value: "2" }
const _hoisted_6 = { value: "3" }
const _hoisted_7 = {
  key: 0,
  class: "forecast-accuracy-modal__average"
}
const _hoisted_8 = { class: "forecast-accuracy-modal__average-number" }
const _hoisted_9 = { class: "forecast-accuracy-modal__content" }
const _hoisted_10 = { class: "forecast-accuracy-modal__loading" }
const _hoisted_11 = { class: "forecast-accuracy-modal__error" }
const _hoisted_12 = { class: "forecast-accuracy-modal__error-message" }

import { ref, computed, watch } from 'vue';
import { i18n } from '@/i18n';
import SvgLoader from '@/utils/SvgLoader.vue';
import EzTable from '@/corePagesModule/components/ezTable/EzTable.vue';
import EzHeader from '@/corePagesModule/components/ezTable/EzHeader.vue';
import EzCell from '@/corePagesModule/components/ezTable/EzCell.vue';
import EzTableRow from '@/corePagesModule/components/ezTable/EzTableRow.vue';
import EzTableLoadingState from '@/corePagesModule/components/ezTable/EzTableLoadingState.vue';
import EzTableErrorState from '@/corePagesModule/components/ezTable/EzTableErrorState.vue';
import { LoadingStatus } from '@/corePagesModule/components/ezTable/types';
import { IForecastAccuracyHistory } from '../../domains/entities/ForecastAccuracy';
import { ACCURACY_THRESHOLDS } from '../../constants/forecastAccuracy';
import TopSlideDownModal from '@/corePagesModule/components/TopSlideDownModal.vue';

/**
 * ForecastAccuracyHistoryModal コンポーネントのプロパティ
 */
interface Props {
  /** モーダルが開いているかどうか */
  isOpen: boolean;
  /** 履歴データ */
  historyData: IForecastAccuracyHistory | null;
  /** ローディング状態 */
  loadingStatus: LoadingStatus;
}

/**
 * ForecastAccuracyHistoryModal コンポーネントのイベント
 */
interface Emits {
  /** モーダルを閉じる */
  close: [];
  /** 期間変更 */
  'change-period': [period: 1 | 2 | 3];
  /** リトライ */
  retry: [];
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ForecastAccuracyHistoryModal',
  props: {
    isOpen: { type: Boolean },
    historyData: {},
    loadingStatus: {}
  },
  emits: ['close', 'change-period', 'retry'],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;

// i18n関数
const t = i18n.t;

// 選択中の期間
const selectedPeriod = ref<1 | 2 | 3>(1);

/**
 * テーブル用データを転置形式で生成
 * @description 月別データを項目別行形式に変換
 */
const tableData = computed(() => {
  if (!props.historyData?.historyData) return [];

  // 過去1年分のデータを古い月から新しい月の順番で取得
  const sortedData = [...props.historyData.historyData]
    .sort((a, b) => new Date(a.yearMonth).getTime() - new Date(b.yearMonth).getTime())
    .slice(0, 12);

  return [
    {
      label: t('販売実績'),
      type: 'salesResult',
      data: sortedData.map((item) => ({
        value: item.salesResult,
        yearMonth: item.yearMonth,
      })),
    },
    {
      label: t('内示'),
      type: 'forecast',
      data: sortedData.map((item) => ({
        value: item.forecast,
        yearMonth: item.yearMonth,
      })),
    },
    {
      label: t('内示正答率'),
      type: 'accuracyRate',
      data: sortedData.map((item) => ({
        value: item.accuracyRate,
        yearMonth: item.yearMonth,
      })),
    },
  ];
});

/**
 * テーブルヘッダー定義（動的に月列を生成）
 */
const tableHeaders = computed(() => {
  if (!props.historyData?.historyData) return [{ key: 'label', label: '' }];

  // 過去1年分のデータを古い月から新しい月の順番でソート
  const sortedData = [...props.historyData.historyData]
    .sort((a, b) => new Date(a.yearMonth).getTime() - new Date(b.yearMonth).getTime())
    .slice(0, 12);

  const headers = [{ key: 'label', label: '' }]; // 最初の列は空白

  sortedData.forEach((item) => {
    headers.push({
      key: item.yearMonth,
      label: formatYearMonthHeader(item.yearMonth),
    });
  });

  return headers;
});

/**
 * grid-template-columnsを動的に生成
 */
const gridTemplateColumns = computed((): string[] => {
  const columnCount = tableHeaders.value.length - 1; // 最初の列はラベル用なので除外
  // 各列の幅を90pxに設定
  const gridTemplateColumns = Array(columnCount).fill('90px');
  return ['200px', ...gridTemplateColumns]; // 最初の列はラベル用に200px
});

/**
 * ヘッダー用年月フォーマット（YYYY/MM形式）
 * @param yearMonth - YYYY-MM形式の年月
 * @returns YYYY/MM形式の年月文字列
 */
const formatYearMonthHeader = (yearMonth: string): string => {
  return yearMonth.replace('-', '/');
};

/**
 * 数値のフォーマット
 * @param value - 数値
 * @returns カンマ区切りの数値文字列
 */
const formatNumber = (value: number): string => {
  return value.toLocaleString();
};

/**
 * 正答率に応じたスタイルを取得
 * @param rate - 正答率
 * @returns スタイルオブジェクト
 */
const getAccuracyStyle = (rate: number) => {
  if (rate <= ACCURACY_THRESHOLDS.CRITICAL) {
    return {
      backgroundColor: 'var(--color-semantic-error-subdued)',
      color: 'var(--color-semantic-error)',
      fontWeight: 'bold',
    };
  } else if (rate > ACCURACY_THRESHOLDS.CRITICAL && rate <= ACCURACY_THRESHOLDS.WARNING) {
    return {
      color: 'var(--color-warning)',
      fontWeight: 'bold',
    };
  }
  return { fontWeight: 'bold' };
};

/**
 * モーダルを閉じる
 */
const handleClose = () => {
  emit('close');
};

/**
 * 期間変更処理
 */
const handlePeriodChange = () => {
  emit('change-period', selectedPeriod.value);
};

/**
 * リトライ処理
 */
const handleRetry = () => {
  emit('retry');
};

// 履歴データの変更を監視して期間を同期
watch(
  () => props.historyData?.selectedPeriod,
  (newPeriod) => {
    if (newPeriod) {
      selectedPeriod.value = newPeriod as 1 | 2 | 3;
    }
  },
  { immediate: true }
);

// モーダルが開かれたときに期間を1にリセット
watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      selectedPeriod.value = 1;
    }
  },
  { immediate: true }
);

const modalStyle = {
  display: 'grid',
  alignItems: 'center',
  justifyContent: 'center',
  gridTemplateColumns: 'minmax(calc(200px + 12 * 90px), auto)',
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(TopSlideDownModal, {
    "is-open": _ctx.isOpen,
    style: modalStyle
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("h3", _hoisted_2, _toDisplayString(_unref(t)('内示正答率履歴(モーダルタイトル)')), 1),
        _createElementVNode("div", _hoisted_3, [
          _withDirectives(_createElementVNode("select", {
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedPeriod).value = $event)),
            class: "forecast-accuracy-modal__period-select",
            onChange: handlePeriodChange
          }, [
            _createElementVNode("option", _hoisted_4, _toDisplayString(_unref(t)('1ヶ月')), 1),
            _createElementVNode("option", _hoisted_5, _toDisplayString(_unref(t)('2ヶ月')), 1),
            _createElementVNode("option", _hoisted_6, _toDisplayString(_unref(t)('3ヶ月')), 1)
          ], 544), [
            [_vModelSelect, selectedPeriod.value]
          ]),
          (_ctx.historyData)
            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                _createTextVNode(_toDisplayString(_unref(t)('平均:')) + " ", 1),
                _createElementVNode("span", _hoisted_8, _toDisplayString(formatNumber(_ctx.historyData.averageAccuracy)), 1),
                _cache[1] || (_cache[1] = _createTextVNode(" % "))
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("button", {
            class: "forecast-accuracy-modal__close-btn",
            onClick: handleClose
          }, [
            _createVNode(SvgLoader, { name: "close" })
          ])
        ])
      ]),
      _createElementVNode("div", _hoisted_9, [
        _createElementVNode("div", {
          class: _normalizeClass(["forecast-accuracy-modal__table-container", { 'forecast-accuracy-modal__table-container--border': _ctx.loadingStatus.type === 'success' }])
        }, [
          _createVNode(EzTable, {
            items: tableData.value,
            "grid-template-columns": gridTemplateColumns.value,
            "loading-status": _ctx.loadingStatus,
            class: "forecast-accuracy-history-table"
          }, {
            header: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tableHeaders.value, (header) => {
                return (_openBlock(), _createBlock(EzHeader, {
                  key: header.key,
                  class: "forecast-accuracy-table__header"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(header.label), 1)
                  ], undefined, true),
                  _: 2
                }, 1024))
              }), 128))
            ]),
            body: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tableData.value, (item) => {
                return (_openBlock(), _createBlock(EzTableRow, {
                  key: item.type,
                  "grid-template-columns": gridTemplateColumns.value,
                  class: "forecast-accuracy-table__row"
                }, {
                  default: _withCtx(() => [
                    _createVNode(EzCell, { class: "forecast-accuracy-table__cell forecast-accuracy-table__cell--label" }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(item.label), 1)
                      ], undefined, true),
                      _: 2
                    }, 1024),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(item.data, (dataPoint) => {
                      return (_openBlock(), _createBlock(EzCell, {
                        key: dataPoint.yearMonth,
                        class: "forecast-accuracy-table__cell forecast-accuracy-table__cell--number",
                        "is-number": "",
                        style: _normalizeStyle(item.type === 'accuracyRate' ? getAccuracyStyle(dataPoint.value) : {})
                      }, {
                        default: _withCtx(() => [
                          (item.type === 'accuracyRate')
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                _createTextVNode(_toDisplayString(dataPoint.value) + "% ", 1)
                              ], 64))
                            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                _createTextVNode(_toDisplayString(formatNumber(dataPoint.value)), 1)
                              ], 64))
                        ], undefined, true),
                        _: 2
                      }, 1032, ["style"]))
                    }), 128))
                  ], undefined, true),
                  _: 2
                }, 1032, ["grid-template-columns"]))
              }), 128))
            ]),
            loading: _withCtx(() => [
              _createElementVNode("div", _hoisted_10, [
                _createVNode(EzTableLoadingState)
              ])
            ]),
            error: _withCtx(() => [
              _createElementVNode("div", _hoisted_11, [
                _createVNode(EzTableErrorState, { onRetry: handleRetry }),
                _createElementVNode("p", _hoisted_12, _toDisplayString(_unref(t)('履歴の取得に失敗しました')), 1)
              ])
            ]),
            _: 1
          }, 8, ["items", "grid-template-columns", "loading-status"])
        ], 2)
      ])
    ], undefined, true),
    _: 1
  }, 8, ["is-open"]))
}
}

})